import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { CircleQuestionIcon } from '@tanium/react-graphics';
import styled from '@tanium/react-emotion';
import { css } from '@tanium/style-system';
import { ThemeColors, ThemeSpace } from '@tanium/theme-data';
import { RED } from '@tanium/colors';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { AppHeaderStyle } from './AppHeaderStyle';
import { TitleBoldText } from '../../TextStyles';
import { useAuthContext } from '../../auth';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTenant } from '../../hooks/useTenant';
import { DocLinks } from '../../utilities/constants';
import { getRegionDisplayName } from '../../utilities/constants';
import { DeleteAccountModal } from '../../pages/ManageAccount/DeleteAccountModal';
import { UserInfoModal } from './UserInfoModal';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const Container = styled(Box)(
  css({
    color: ThemeColors.Active2,
    bg: ThemeColors.HeaderTier2,
    height: `${AppHeaderStyle.containerHeight}px`,
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    whiteSpace: 'nowrap',
  })
);

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const MenuLink = styled(Link)(
  css({
    p: ThemeSpace[1],
    color: ThemeColors.Light0,
    textDecoration: 'none',
    width: '100%',
    display: 'block',
    ':hover': {
      bg: ThemeColors.HeaderTier2,
      color: ThemeColors.HeaderLinkHover,
    },
  })
);

// A plain button rather than the link-variant Button, whose own font size and link color would
// override the header text style.
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const UsernameButton = styled('button')(
  css({
    p: 0,
    border: 'none',
    bg: 'transparent',
    color: 'inherit',
    font: 'inherit',
    cursor: 'pointer',
    ':hover': {
      color: ThemeColors.HeaderLinkHover,
    },
  })
);

export const AppHeader: React.VFC = () => {
  const { t } = useTranslation();
  const { loginUsername, signOut } = useAuthContext();
  const { loadTenant, tenant } = useTenant();
  const [userInfoModalOpen, setUserInfoModalOpen] = useState(false);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);

  return (
    <Box position="fixed" top={0} width="100%" zIndex={1}>
      <Box bg={RED} height={`${AppHeaderStyle.borderHeight}px`} width="100%" />
      <Container>
        <Box mx={3}>
          <Logo />
        </Box>
        <TitleBoldText>{t('Tanium Account')}</TitleBoldText>
        <Box mx={3} flexGrow={1} />
        {loadTenant.status === AsyncStatus.Success && (
          <>
            <Box mx={3}>{`${t('Region')}: ${t(getRegionDisplayName(tenant?.region))}`}</Box>
            <Box ml={ThemeSpace.minor[1]} mr={ThemeSpace.minor[1]}>
              |
            </Box>
          </>
        )}
        <Box mx={3} display="flex" alignItems="center">
          {loginUsername && (
            <UsernameButton type="button" onClick={() => setUserInfoModalOpen(true)}>
              {loginUsername}
            </UsernameButton>
          )}
          <Box ml={ThemeSpace.minor[1]} display="flex" alignItems="center">
            <TooltippedIconButton
              height={16}
              width={16}
              icon={CircleQuestionIcon}
              tooltipContent={t('Tanium Documentation')}
              label={t('Tanium Documentation')}
              onClick={() => {
                window.open(DocLinks.DOCS_HOME, '_blank', 'noopener,noreferrer');
              }}
              style={{ color: ThemeColors.Light0 }}
            />
          </Box>
        </Box>
        <Box ml={ThemeSpace.minor[1]} mr={ThemeSpace.minor[1]}>
          |
        </Box>
        <Box mx={3}>
          <MenuLink to="" onClick={signOut}>
            {t('Logout')}
          </MenuLink>
        </Box>
      </Container>
      <UserInfoModal
        isOpen={userInfoModalOpen}
        onRequestClose={() => setUserInfoModalOpen(false)}
        onDeleteAccount={() => {
          // Swap rather than stack the modals.
          setUserInfoModalOpen(false);
          setDeleteAccountModalOpen(true);
        }}
      />
      {/* User Info re-reads eligibility when it next opens, so a failed delete just closes. */}
      {tenant && (
        <DeleteAccountModal
          isOpen={deleteAccountModalOpen}
          workspace={tenant.workspace}
          onRequestClose={() => setDeleteAccountModalOpen(false)}
          onDeleteFailed={() => setDeleteAccountModalOpen(false)}
        />
      )}
    </Box>
  );
};
